/**
 * The browser backend's video half (webVoice.ts): screen share (Go Live) and
 * camera on the same RTCPeerConnection as the voice
 * (docs/decisions/2026-10-29-screen-share-and-video.md §4.1-§4.3).
 *
 * - Video m-lines are added only while used, by renegotiation the client
 *   starts (`sdp {req, offer, layout}` → `sdp_answer`), one at a time
 *   (changes while one is in flight are folded into the next).
 * - A publication is asked for first (`publish`), then its send-only
 *   m-line(s) are added paused (`encodings[0].active = false`); comms-voice
 *   turns them on with `send` only while someone receives, so with nobody
 *   watching nothing is uploaded.
 * - Receive slots (`video_slots`, `stream_audio_slots`) are added for what
 *   is on screen now (`visible`) and the screens you watch with sound; one
 *   free for `VOICE_SLOT_LINGER_MS` is removed again, so a call with no
 *   video costs what it did before.
 */
import {
  VOICE_SLOT_LINGER_MS,
  type Pid,
  type VoiceMediaLayout,
  type VoiceMediaSource,
  type VoicePublishCaps,
  type VoiceVideoRef,
} from '@neosocial/protocol';
import {
  DEFAULT_MEDIA_CAPS,
  encodingFor,
  lingerDrop,
  newRecvAudioMids,
  publishCapsOf,
  qualityBox,
  sameRefs,
  slotsWanted,
  VISIBLE_MAX,
  vp8Only,
  withStereo,
} from './media';
import {
  clampStreamVolume,
  elementVolumeWorks,
  MediaSupersededError,
  slotPlayback,
  VoiceDeniedError,
  type ClientMessage,
  type ServerMessage,
  type StreamQuality,
  type VoiceMedia,
} from './types';

interface Pub {
  source: VoiceMediaSource;
  video: MediaStreamTrack;
  audio: MediaStreamTrack | null;
  vtr: RTCRtpTransceiver;
  atr: RTCRtpTransceiver | null;
  caps: VoicePublishCaps;
  /** What comms-voice said last (`send`); both start paused. */
  sendVideo: boolean;
  sendAudio: boolean;
  /** The quality picked in place (≤ the caps), or null: the caps. */
  quality: StreamQuality | null;
  /** The capture size the encoding was scaled for. */
  size: string;
  timer: ReturnType<typeof setInterval> | undefined;
}

interface VSlot {
  tr: RTCRtpTransceiver;
  pid: Pid | null;
  source: VoiceMediaSource | null;
  freeSince: number;
  /** A fresh stream per assignment, so a reassigned slot never shows the last person's frame. */
  stream: MediaStream | null;
}

interface SSlot {
  tr: RTCRtpTransceiver;
  pid: Pid | null;
  freeSince: number;
  audio: HTMLAudioElement;
  /** Web Audio boost above 100 % (created on first use), as a voice slot's. */
  boost: { src: MediaStreamAudioSourceNode; gain: GainNode } | null;
}

export interface MediaDeps {
  pc(): RTCPeerConnection | null;
  request(m: ClientMessage & { req: number }, ms?: number): Promise<ServerMessage>;
  send(m: ClientMessage): void;
  nextReq(): number;
  deaf(): boolean;
  outputDevice(): string | null;
  /** The master output volume, 0..2 (a stream's sound plays at its volume times this); default 1. */
  outputVolume?(): number;
  /** How long to wait before retrying an `sdp` refused as `rate_limited`. */
  retryMs?: number;
  /** How often free slots are looked at (they go after `VOICE_SLOT_LINGER_MS`). */
  lingerTickMs?: number;
  /** How often a live capture's size is read (a resized window). */
  sizeTickMs?: number;
}

export interface MediaEngine extends VoiceMedia {
  /** Every message from comms-voice, before the screens see it. */
  onServer(ev: ServerMessage): void;
  /** Deafen or undeafen: stream sound follows. */
  onDeafen(): void;
  /** The master output volume changed: stream sound follows. */
  onOutputVolume(): void;
  setOutputDevice(id: string | null): void;
  teardown(): void;
}

type SinkAudio = HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> };

/** `setCodecPreferences` to VP8 and RTX on a video transceiver, where the browser can. */
function preferVp8(tr: RTCRtpTransceiver): void {
  try {
    const caps =
      (typeof RTCRtpReceiver !== 'undefined' && RTCRtpReceiver.getCapabilities?.('video')) || null;
    if (caps?.codecs?.length && tr.setCodecPreferences)
      tr.setCodecPreferences(vp8Only(caps.codecs));
  } catch {
    /* an engine without codec preferences offers what it has */
  }
}

function stopped(tr: RTCRtpTransceiver): boolean {
  return (
    (tr as RTCRtpTransceiver & { stopped?: boolean }).stopped === true ||
    tr.currentDirection === 'stopped' ||
    tr.direction === 'stopped'
  );
}

export function createMediaEngine(d: MediaDeps, helloMids: ReadonlySet<string>): MediaEngine {
  const pubs = new Map<VoiceMediaSource, Pub>();
  /** Each source's latest publish or unpublish: a publish answered after a newer one adds nothing. */
  const gens = new Map<VoiceMediaSource, number>();
  const bump = (source: VoiceMediaSource) => {
    const g = (gens.get(source) ?? 0) + 1;
    gens.set(source, g);
    return g;
  };
  /** `send` that came before its publication was set up (applied when it is). */
  const sendEarly = new Map<VoiceMediaSource, { video: boolean; audio: boolean }>();
  let vslots: VSlot[] = [];
  let sslots: SSlot[] = [];
  let visible: VoiceVideoRef[] = [];
  const watching = new Set<Pid>();
  const sound = new Map<Pid, boolean>();
  const streamVolumes = new Map<Pid, number>();
  let negotiating: Promise<void> | null = null;
  let again = false;
  let dead = false;
  let lingerTimer: ReturnType<typeof setInterval> | undefined;
  let retryTimer: ReturnType<typeof setTimeout> | undefined;
  const chains = new WeakMap<RTCRtpSender, Promise<void>>();

  /** One `setParameters` at a time per sender (a stale transaction id is refused). */
  function updateParams(
    sender: RTCRtpSender,
    fn: (p: RTCRtpSendParameters) => boolean,
  ): Promise<void> {
    const run = async () => {
      try {
        const p = sender.getParameters();
        if (!p.encodings?.length) p.encodings = [{}] as RTCRtpEncodingParameters[];
        if (fn(p)) await sender.setParameters(p);
      } catch {
        /* not negotiated yet: applied again after the answer */
      }
    };
    const next = (chains.get(sender) ?? Promise.resolve()).then(run);
    chains.set(sender, next);
    return next;
  }

  function setActive(sender: RTCRtpSender, on: boolean): void {
    void updateParams(sender, (p) => {
      const e = p.encodings[0]!;
      if (e.active === on) return false;
      e.active = on;
      return true;
    });
  }

  function applySend(p: Pub): void {
    setActive(p.vtr.sender, p.sendVideo);
    if (p.atr) setActive(p.atr.sender, p.sendAudio);
  }

  /** The box this publication is held to now: the caps, or a lower quality (and its bitrate). */
  function boxOf(p: Pub): VoicePublishCaps {
    return qualityBox(p.caps, p.quality);
  }

  /** Scales the encoding for the capture's size now (a window resized, another tab shared). */
  function rescale(p: Pub, force = false): void {
    const s = p.video.getSettings?.() ?? {};
    const size = `${s.width ?? 0}x${s.height ?? 0}`;
    if (!force && size === p.size) return;
    p.size = size;
    const box = boxOf(p);
    const enc = encodingFor(s, box);
    void updateParams(p.vtr.sender, (prm) => {
      const e = prm.encodings[0]!;
      e.maxBitrate = enc.maxBitrate;
      e.maxFramerate = enc.maxFramerate;
      e.scaleResolutionDownBy = enc.scaleResolutionDownBy;
      return true;
    });
  }

  function layout(): VoiceMediaLayout {
    const l: VoiceMediaLayout = { video_slots: [], stream_audio_slots: [] };
    const scr = pubs.get('screen');
    if (scr?.vtr.mid && !stopped(scr.vtr)) l.screen = scr.vtr.mid;
    if (scr?.atr?.mid && !stopped(scr.atr)) l.screen_audio = scr.atr.mid;
    const cam = pubs.get('camera');
    if (cam?.vtr.mid && !stopped(cam.vtr)) l.camera = cam.vtr.mid;
    for (const s of vslots) if (s.tr.mid && !stopped(s.tr)) l.video_slots.push(s.tr.mid);
    for (const s of sslots) if (s.tr.mid && !stopped(s.tr)) l.stream_audio_slots.push(s.tr.mid);
    return l;
  }

  async function negotiateOnce(): Promise<void> {
    const conn = d.pc();
    if (!conn || dead) return;
    const offer = await conn.createOffer();
    if (d.pc() !== conn || dead) return;
    // stereo on the streams' sound slots, as the answer's sprop-stereo says (§4.1)
    const raw = offer.sdp ?? '';
    const sdp = withStereo(raw, newRecvAudioMids(raw, helloMids));
    await conn.setLocalDescription({ type: 'offer', sdp });
    if (d.pc() !== conn || dead) return;
    const l = layout();
    let ans: ServerMessage;
    try {
      ans = await d.request(
        { t: 'sdp', req: d.nextReq(), offer: conn.localDescription?.sdp ?? sdp, layout: l },
        10_000,
      );
    } catch (e) {
      // the offer stays ours only: back to the last agreed state
      await conn
        .setLocalDescription({ type: 'rollback' } as RTCSessionDescriptionInit)
        .catch(() => {});
      if (e instanceof VoiceDeniedError && e.reason === 'rate_limited' && !dead) {
        clearTimeout(retryTimer);
        retryTimer = setTimeout(() => void negotiate().catch(() => {}), d.retryMs ?? 2000);
      }
      throw e;
    }
    if (d.pc() !== conn || dead || ans.t !== 'sdp_answer') return;
    await conn.setRemoteDescription({ type: 'answer', sdp: ans.answer });
    // parameters set before the m-line existed are set again now
    for (const p of pubs.values()) {
      rescale(p, true);
      applySend(p);
    }
  }

  /** Renegotiates now, or right after the one in flight (changes are folded together). */
  function negotiate(): Promise<void> {
    if (negotiating) {
      again = true;
      return negotiating;
    }
    const run = (async () => {
      do {
        again = false;
        await negotiateOnce();
      } while (again && !dead);
    })();
    negotiating = run.finally(() => {
      negotiating = null;
    });
    return negotiating;
  }

  function soundStreams(): number {
    let n = 0;
    for (const p of watching) if (sound.get(p)) n++;
    return n;
  }

  /** Adds the receive slots worth having now (§2.1 rule 7), and watches the free ones. */
  function ensureSlots(): void {
    const conn = d.pc();
    if (!conn || dead) return;
    const want = slotsWanted({
      visible: visible.length,
      soundStreams: soundStreams(),
      deaf: d.deaf(),
    });
    let changed = false;
    while (vslots.length < want.video) {
      const tr = conn.addTransceiver('video', { direction: 'recvonly' });
      preferVp8(tr);
      vslots.push({ tr, pid: null, source: null, freeSince: Date.now(), stream: null });
      changed = true;
    }
    while (sslots.length < want.audio) {
      const tr = conn.addTransceiver('audio', { direction: 'recvonly' });
      const audio = new Audio() as SinkAudio;
      audio.autoplay = true;
      audio.srcObject = new MediaStream([tr.receiver.track]);
      const out = d.outputDevice();
      if (out) void audio.setSinkId?.(out).catch(() => {});
      const s: SSlot = { tr, pid: null, freeSince: Date.now(), audio, boost: null };
      applyStreamAudio(s);
      sslots.push(s);
      changed = true;
    }
    if (changed) void negotiate().catch(() => {});
    const extra = vslots.length > want.video || sslots.length > want.audio;
    if (extra && lingerTimer === undefined)
      lingerTimer = setInterval(linger, d.lingerTickMs ?? 5000);
    else if (!extra && lingerTimer !== undefined) {
      clearInterval(lingerTimer);
      lingerTimer = undefined;
    }
  }

  /** Removes slots free for `VOICE_SLOT_LINGER_MS` beyond what is wanted now. */
  function linger(): void {
    const want = slotsWanted({
      visible: visible.length,
      soundStreams: soundStreams(),
      deaf: d.deaf(),
    });
    const now = Date.now();
    const dv = lingerDrop(vslots, want.video, now, VOICE_SLOT_LINGER_MS);
    const da = lingerDrop(sslots, want.audio, now, VOICE_SLOT_LINGER_MS);
    for (const s of dv) s.tr.stop();
    for (const s of da) {
      dropAudio(s);
      s.tr.stop();
    }
    if (dv.length) vslots = vslots.filter((s) => !dv.includes(s));
    if (da.length) sslots = sslots.filter((s) => !da.includes(s));
    if (dv.length || da.length) void negotiate().catch(() => {});
    ensureSlots();
  }

  // one playback context for streams turned above 100 % (as webVoice's boost)
  let playCtx: AudioContext | null = null;
  function boostFor(s: SSlot): SSlot['boost'] {
    if (s.boost) return s.boost;
    try {
      playCtx ??= new AudioContext();
      const out = d.outputDevice();
      if (out)
        void (playCtx as AudioContext & { setSinkId?: (id: string) => Promise<void> })
          .setSinkId?.(out)
          .catch(() => {});
      const src = playCtx.createMediaStreamSource(new MediaStream([s.tr.receiver.track]));
      const gain = playCtx.createGain();
      src.connect(gain).connect(playCtx.destination);
      s.boost = { src, gain };
      void playCtx.resume().catch(() => {});
    } catch {
      s.boost = null;
    }
    return s.boost;
  }

  /**
   * A stream-sound slot at its stream's volume (0..2): up to 100 % the
   * audio element's own volume, above it a GainNode (the element stays
   * attached, muted); deafened or at 0, silent.
   */
  function applyStreamAudio(s: SSlot): void {
    const v = (s.pid === null ? 1 : (streamVolumes.get(s.pid) ?? 1)) * (d.outputVolume?.() ?? 1);
    const muted = d.deaf() || v <= 0;
    const plan = slotPlayback(v, muted, elementVolumeWorks());
    const boost = plan.gain !== null ? boostFor(s) : s.boost;
    if (plan.gain !== null && !boost) {
      // no Web Audio: play at 100 % rather than not at all
      s.audio.muted = muted;
      s.audio.volume = 1;
      return;
    }
    s.audio.muted = plan.elementMuted;
    s.audio.volume = plan.elementVolume;
    if (boost) boost.gain.gain.value = plan.gain ?? 0;
  }

  /** A stream-sound slot's playback ends (its element and any boost). */
  function dropAudio(s: SSlot): void {
    s.audio.pause();
    s.audio.srcObject = null;
    if (s.boost) {
      try {
        s.boost.src.disconnect();
        s.boost.gain.disconnect();
      } catch {
        /* already gone */
      }
      s.boost = null;
    }
  }

  /** Drops a publication's sections (the tracks stay the caller's). */
  function dropPub(source: VoiceMediaSource): boolean {
    sendEarly.delete(source);
    const p = pubs.get(source);
    if (!p) return false;
    pubs.delete(source);
    clearInterval(p.timer);
    try {
      p.vtr.stop();
      p.atr?.stop();
    } catch {
      /* already closed */
    }
    return true;
  }

  /**
   * `publish {on:false}`: comms-voice ends the publication (stopping one not
   * live is fine there). Not a request: said on a resumed socket too.
   */
  function stopThere(source: VoiceMediaSource): void {
    d.send({ t: 'publish', req: d.nextReq(), source, on: false });
  }

  async function publish(
    source: VoiceMediaSource,
    tracks: { video: MediaStreamTrack; audio?: MediaStreamTrack | null },
    quality: StreamQuality | null = null,
  ): Promise<VoicePublishCaps> {
    const conn = d.pc();
    if (!conn || dead) throw new VoiceDeniedError('unsupported');
    const gen = bump(source);
    const audio = source === 'screen' ? (tracks.audio ?? null) : null;
    let ev: ServerMessage;
    try {
      ev = await d.request({
        t: 'publish',
        req: d.nextReq(),
        source,
        on: true,
        ...(audio ? { audio: true } : {}),
      });
    } catch (e) {
      // no answer in time: it may have gone live there all the same (a
      // refusal published nothing)
      if (!(e instanceof VoiceDeniedError) && d.pc() === conn && !dead) stopThere(source);
      throw e;
    }
    if (d.pc() !== conn || dead) throw new Error('left');
    // stopped while comms-voice said yes: its `publish off` went after this
    // one, so it is not live there; nothing is added here (QA D2)
    if (gens.get(source) !== gen) throw new MediaSupersededError();
    const caps =
      ev.t === 'published' && ev.caps ? ev.caps : publishCapsOf(source, DEFAULT_MEDIA_CAPS);
    // live already (a restart): the new tracks replace the old sections
    dropPub(source);
    const enc = encodingFor(tracks.video.getSettings?.() ?? {}, qualityBox(caps, quality));
    const vtr = conn.addTransceiver(tracks.video, {
      direction: 'sendonly',
      sendEncodings: [{ active: false, ...enc }],
    });
    preferVp8(vtr);
    void updateParams(vtr.sender, (p) => {
      (p as RTCRtpSendParameters & { degradationPreference?: string }).degradationPreference =
        source === 'screen' ? 'maintain-resolution' : 'balanced';
      return true;
    });
    const atr = audio
      ? conn.addTransceiver(audio, {
          direction: 'sendonly',
          sendEncodings: [{ active: false, maxBitrate: (caps.audio_kbps ?? 64) * 1000 }],
        })
      : null;
    const s = tracks.video.getSettings?.() ?? {};
    const p: Pub = {
      source,
      video: tracks.video,
      audio,
      vtr,
      atr,
      caps,
      sendVideo: false,
      sendAudio: false,
      quality,
      size: `${s.width ?? 0}x${s.height ?? 0}`,
      timer: undefined,
    };
    const early = sendEarly.get(source);
    if (early) {
      p.sendVideo = early.video;
      p.sendAudio = early.audio;
      sendEarly.delete(source);
    }
    pubs.set(source, p);
    p.timer = setInterval(() => rescale(p), d.sizeTickMs ?? 2000);
    try {
      await negotiate();
    } catch (e) {
      // comms-voice said yes but the sections never got there (a refused,
      // rate-limited or unanswered `sdp`): it is ended there too, or it
      // would stay LIVE with no video for everyone (QA D2)
      if (pubs.get(source) === p) {
        dropPub(source);
        stopThere(source);
      }
      throw e;
    }
    return caps;
  }

  function unpublish(source: VoiceMediaSource): void {
    // a publish still waiting for its answer adds nothing now
    bump(source);
    const had = dropPub(source);
    // comms-voice first (forwarding stops at once), then the sections go
    stopThere(source);
    if (had) void negotiate().catch(() => {});
  }

  async function watch(pid: Pid, on: boolean): Promise<boolean> {
    const ev = await d.request({ t: 'watch', req: d.nextReq(), pid, on });
    return ev.t === 'watch_set' ? ev.on : on;
  }

  function setVisible(sources: VoiceVideoRef[]): void {
    const next = sources.slice(0, VISIBLE_MAX).map((r) => ({ pid: r.pid, source: r.source }));
    if (sameRefs(next, visible)) return;
    visible = next;
    d.send({ t: 'visible', sources: next });
    ensureSlots();
  }

  function stream(pid: Pid, source: VoiceMediaSource): MediaStream | null {
    const s = vslots.find((x) => x.pid === pid && x.source === source);
    if (!s) return null;
    s.stream ??= new MediaStream([s.tr.receiver.track]);
    return s.stream;
  }

  function setStreamVolume(pid: Pid, v: number): void {
    streamVolumes.set(pid, clampStreamVolume(v));
    for (const s of sslots) if (s.pid === pid) applyStreamAudio(s);
  }

  function setQuality(source: VoiceMediaSource, q: StreamQuality): void {
    const p = pubs.get(source);
    if (!p) return;
    p.quality = q;
    const box = boxOf(p);
    void p.video.applyConstraints?.({ frameRate: { max: box.max_fps } }).catch(() => {});
    rescale(p, true);
  }

  function onServer(ev: ServerMessage): void {
    switch (ev.t) {
      case 'welcome':
        for (const m of ev.members as Array<{ pid: Pid; screen_audio?: boolean }>)
          sound.set(m.pid, !!m.screen_audio);
        break;
      case 'media':
        sound.set(ev.pid, ev.screen && ev.screen_audio);
        ensureSlots();
        break;
      case 'watch_set':
        if (ev.on) watching.add(ev.pid);
        else watching.delete(ev.pid);
        ensureSlots();
        break;
      case 'left':
        watching.delete(ev.pid);
        sound.delete(ev.pid);
        for (const s of vslots)
          if (s.pid === ev.pid) {
            s.pid = null;
            s.source = null;
            s.stream = null;
            s.freeSince = Date.now();
          }
        ensureSlots();
        break;
      case 'vslot': {
        const s = vslots.find((x) => x.tr.mid === ev.mid);
        if (!s) break;
        const pid = ev.pid ?? null;
        const source = pid === null ? null : (ev.source ?? 'camera');
        if (s.pid !== pid || s.source !== source) s.stream = null;
        s.pid = pid;
        s.source = source;
        if (pid === null) s.freeSince = Date.now();
        break;
      }
      case 'sslot': {
        const s = sslots.find((x) => x.tr.mid === ev.mid);
        if (!s) break;
        s.pid = ev.pid ?? null;
        if (s.pid === null) s.freeSince = Date.now();
        applyStreamAudio(s);
        break;
      }
      case 'send': {
        const p = pubs.get(ev.source);
        if (!p) {
          sendEarly.set(ev.source, { video: ev.video, audio: !!ev.audio });
          break;
        }
        p.sendVideo = ev.video;
        p.sendAudio = !!ev.audio;
        applySend(p);
        break;
      }
      case 'unpublished':
        if (dropPub(ev.source)) void negotiate().catch(() => {});
        break;
      case 'moved':
        // comms-voice ended every watch and slot before the move (§4.6)
        watching.clear();
        break;
    }
  }

  return {
    publish,
    unpublish,
    watch,
    setVisible,
    stream,
    setStreamVolume,
    setQuality,
    onServer,
    onDeafen() {
      for (const s of sslots) applyStreamAudio(s);
      ensureSlots();
    },
    onOutputVolume() {
      for (const s of sslots) applyStreamAudio(s);
    },
    setOutputDevice(id) {
      for (const s of sslots) void (s.audio as SinkAudio).setSinkId?.(id ?? '').catch(() => {});
      void (playCtx as (AudioContext & { setSinkId?: (id: string) => Promise<void> }) | null)
        ?.setSinkId?.(id ?? '')
        .catch(() => {});
    },
    teardown() {
      dead = true;
      clearInterval(lingerTimer);
      clearTimeout(retryTimer);
      lingerTimer = undefined;
      for (const p of pubs.values()) clearInterval(p.timer);
      pubs.clear();
      for (const s of sslots) dropAudio(s);
      void playCtx?.close().catch(() => {});
      playCtx = null;
      vslots = [];
      sslots = [];
    },
    get publishing() {
      return [...pubs.keys()];
    },
  };
}
